import { Link } from 'react-router';
import { cn } from '@/lib/cn';
import { Countdown } from './Countdown';
import { Podium } from './Podium';
import { formatDateRange, formatMoney } from '../format';
import type { Race } from '../api';

interface RaceCardProps {
  race: Race;
  /** The home page shows one large card; the list shows several. */
  featured?: boolean;
}

const phaseBadges: Record<Race['phase'], { text: string; className: string }> = {
  running: { text: 'DEVAM EDİYOR', className: 'bg-emerald-500/15 text-emerald-300' },
  upcoming: { text: 'YAKINDA', className: 'bg-sky-500/15 text-sky-300' },
  awaiting_results: { text: 'SONUÇLAR AÇIKLANIYOR', className: 'bg-amber-500/15 text-amber-300' },
  finished: { text: 'TAMAMLANDI', className: 'bg-surface-700/60 text-surface-300' },
};

/**
 * One race, as a card: the pool, the clock, and who is winning.
 *
 * The clock changes meaning with the phase — counting down to the START before
 * it begins and to the END while it runs — because a countdown to something that
 * already happened is the kind of detail that makes a site feel abandoned.
 */
export function RaceCard({ race, featured = false }: RaceCardProps) {
  const badge = phaseBadges[race.phase];

  return (
    <article
      data-tilt
      className={cn(
        'race-card relative overflow-hidden border border-surface-800',
        featured ? 'p-5 sm:p-7' : 'p-5',
      )}
      style={{
        // The site's accent, kept faint: it identifies the partner without
        // turning the card into their advertisement.
        boxShadow: `inset 0 1px 0 0 ${race.siteBrandColor}22`,
      }}
    >
      <div className="flex flex-wrap items-center gap-3">
        {race.siteIconUrl ? (
          <img
            src={race.siteIconUrl}
            alt=""
            width={36}
            height={36}
            loading="lazy"
            className="size-9 rounded-full object-cover"
          />
        ) : null}

        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-widest text-surface-500">{race.siteName}</p>
          <h3
            className={cn(
              'truncate font-semibold tracking-tight text-surface-50',
              featured ? 'text-xl sm:text-2xl' : 'text-lg',
            )}
          >
            {race.title}
          </h3>
        </div>

        <span className={cn('rounded-full px-2.5 py-1 text-xs font-bold', badge.className)}>
          {badge.text}
        </span>
      </div>

      <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-surface-500">Ödül havuzu</p>
          <p
            className={cn(
              'font-bold tracking-tight text-brand-400',
              featured ? 'text-4xl sm:text-5xl' : 'text-3xl sm:text-4xl',
            )}
          >
            {formatMoney(race.prizePool, race.currency)}
          </p>
          <p className="mt-1 text-xs text-surface-500">
            {formatDateRange(race.startsAtUtc, race.endsAtUtc)}
          </p>
        </div>

        <div className="flex flex-col items-end gap-1">
          <p className="text-xs uppercase tracking-widest text-surface-500">
            {race.phase === 'upcoming' ? 'Başlamasına' : 'Bitmesine'}
          </p>
          {race.phase === 'finished' || race.phase === 'awaiting_results' ? (
            <p className="text-sm font-medium text-surface-400">
              {race.phase === 'finished' ? 'Sona erdi' : 'Sonuçlar doğrulanıyor'}
            </p>
          ) : (
            <Countdown
              targetIso={race.phase === 'upcoming' ? race.startsAtUtc : race.endsAtUtc}
              overLabel="Sona erdi"
            />
          )}
        </div>
      </div>

      {race.podium.length > 0 ? (
        <div className="mt-7">
          <Podium rows={race.podium} currency={race.currency} />
        </div>
      ) : (
        <p className="mt-7 rounded-lg border border-dashed border-surface-800 py-6 text-center text-sm text-surface-500">
          {race.phase === 'upcoming' ? 'Yarışma henüz başlamadı.' : 'Sıralama henüz yüklenmedi.'}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-surface-500">
          {race.participantCount > 0
            ? `${new Intl.NumberFormat('tr-TR').format(race.participantCount)} katılımcı`
            : 'Katılımcı bekleniyor'}
        </p>

        <Link to={`/wager-races/${race.slug}`} className="gf-button gf-button-secondary">
          Tüm sıralamayı gör
        </Link>
      </div>
    </article>
  );
}
